import {createAsyncThunk} from '@reduxjs/toolkit';
import {getFacilitiesBreakdownReport} from 'api/accounting/accounting';

/** @namespace Accounting.Actions */

/**
 * Asynchronously fetches facilities breakdown data from the accounting API.
 *
 * @param {Object} options - The options for fetching data.
 * @param {number} options.page - The page number of the data to fetch.
 * @param {string} options.toDate - The end date for the data range.
 * @param {string} options.fromDate - The start date for the data range.
 * @param {number} options.perPage - The number of data entries to fetch per page.
 * @param {Array<number>} options.facilityIds - An array of facility IDs to filter the data.
 * @param {Function} options.handleAbortSignal - A function to handle aborting the request.
 * @param {AbortController.prototype.signal} options.abortSignal - The cancel token to cancel the request.
 * @returns {} - A promise that resolves to the fetched data or rejects with an error.
 */
export const getFacilitiesBreakDownData = createAsyncThunk(
    'accounting/getBreakDownData',
    async (
        {
            page,
            toDate,
            fromDate,
            perPage,
            facilityIds,
            handleAbortSignal,
            abortSignal,
        },
        {rejectWithValue, signal}
    ) => {
        try {
            // check if the request has been aborted
            handleAbortSignal(signal);

            const response = await getFacilitiesBreakdownReport(
                {
                    page,
                    toDate,
                    fromDate,
                    perPage,
                    facilityIds,
                },
                {signal: abortSignal}
            );

            return response;
        } catch (error) {
            return rejectWithValue(error);
        }
    }
);
